import { EventEmitter } from 'node:events';
import WebSocket from 'ws';

/** Configuration for the stream relay. */
export interface StreamRelayOptions {
  /** The engine to relay events from. */
  engine: EventEmitter & {
    activeSessionId: string | null;
    isRecording: boolean;
  };
  /** The WebSocket URL to connect to, e.g. ws://127.0.0.1:7601/ws/ingest */
  hubUrl: string;
  /** Reconnect interval in milliseconds (default 3000). */
  reconnectMs?: number;
}

/**
 * Relay that forwards recording engine events to a remote hub over WebSocket.
 *
 * Runs in proxy instances. Connects to the hub's /ws/ingest endpoint and
 * forwards session lifecycle, action, and screencast frame events.
 *
 * Resilient to hub downtime — auto-reconnects on disconnect, drops messages
 * when not connected (no buffering), and resends current session state on
 * reconnect.
 */
export class StreamRelay {
  private readonly engine: StreamRelayOptions['engine'];
  private readonly hubUrl: string;
  private readonly reconnectMs: number;
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  // Bound listeners so we can remove them on shutdown
  private readonly onSessionStart: (data: { sessionId: string }) => void;
  private readonly onSessionEnd: (data: { sessionId: string }) => void;
  private readonly onActionRecorded: (data: {
    sessionId: string;
    action: {
      seq: number;
      tool: string;
      args: Record<string, unknown>;
      timestamp: string;
      pageUrl: string;
      pageTitle: string;
    };
  }) => void;
  private readonly onScreencastFrame: (frame: Buffer) => void;

  constructor(options: StreamRelayOptions) {
    this.engine = options.engine;
    this.hubUrl = options.hubUrl;
    this.reconnectMs = options.reconnectMs ?? 3000;

    // Bind engine listeners
    this.onSessionStart = (data) => {
      this.send({ type: 'session_start', sessionId: data.sessionId });
    };

    this.onSessionEnd = (data) => {
      this.send({ type: 'session_end', sessionId: data.sessionId });
    };

    this.onActionRecorded = (data) => {
      this.send({
        type: 'action',
        seq: data.action.seq,
        tool: data.action.tool,
        args: data.action.args,
        timestamp: data.action.timestamp,
        pageUrl: data.action.pageUrl,
        pageTitle: data.action.pageTitle,
      });
    };

    this.onScreencastFrame = (frame) => {
      this.sendBinary(frame);
    };

    this.engine.on('session-start', this.onSessionStart);
    this.engine.on('session-end', this.onSessionEnd);
    this.engine.on('action-recorded', this.onActionRecorded);
    this.engine.on('screencast-frame', this.onScreencastFrame);

    this.connect();
  }

  /** Stop reconnection attempts, close the WebSocket, and remove engine listeners. */
  shutdown(): void {
    this.stopped = true;

    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.engine.removeListener('session-start', this.onSessionStart);
    this.engine.removeListener('session-end', this.onSessionEnd);
    this.engine.removeListener('action-recorded', this.onActionRecorded);
    this.engine.removeListener('screencast-frame', this.onScreencastFrame);

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private connect(): void {
    if (this.stopped) return;

    const ws = new WebSocket(this.hubUrl);

    ws.on('open', () => {
      if (this.stopped) {
        ws.close();
        return;
      }
      this.ws = ws;
      this.sendCurrentState();
    });

    ws.on('close', () => {
      if (this.ws === ws) this.ws = null;
      this.scheduleReconnect();
    });

    ws.on('error', () => {
      // Suppress connection-refused errors — hub might not be running yet.
      // The 'close' event fires after 'error', which triggers reconnection.
    });
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    if (this.reconnectTimer !== null) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.reconnectMs);
  }

  /** If the engine has an active session, tell the hub about it. */
  private sendCurrentState(): void {
    if (this.engine.isRecording && this.engine.activeSessionId) {
      this.send({
        type: 'session_start',
        sessionId: this.engine.activeSessionId,
      });
    }
  }

  /** Send a JSON message to the hub. Drops silently if not connected. */
  private send(msg: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  /** Send a binary buffer to the hub. Drops silently if not connected. */
  private sendBinary(data: Buffer): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(data);
  }
}
